import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Send, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal, Select } from "@/components/ui";
import { api } from "@/lib/api";
import { useChannels, type Channel, type NotifierPlugin } from "@/lib/tasks";

function usePlugins() {
  return useQuery({ queryKey: ["notifiers"], queryFn: () => api.get<NotifierPlugin[]>("/api/notifiers"), staleTime: Infinity });
}

export default function NotificationSettings() {
  const qc = useQueryClient();
  const channels = useChannels();
  const plugins = usePlugins().data ?? [];
  const [editing, setEditing] = useState<Channel | "new" | null>(null);
  const [tested, setTested] = useState<Record<string, string>>({});
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/notification-channels/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["channels"] }) });
  const test = useMutation({
    mutationFn: (id: string) => api.post(`/api/notification-channels/${id}/test`),
    onSuccess: (_, id) => setTested((t) => ({ ...t, [id]: "Sent" })),
    onError: (e: Error, id) => setTested((t) => ({ ...t, [id]: e.message })),
  });
  return (
    <Card
      title="Notification channels"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> Add channel
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">Where tasks can reach you. The inbox under Tasks is always available.</p>
      {channels.data?.length === 0 && <p className="text-sm text-zinc-500">No channels yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {channels.data?.map((c) => (
          <li key={c.id} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-medium">
                {c.name} <Badge>{plugins.find((p) => p.id === c.plugin)?.label ?? c.plugin}</Badge> {!c.enabled && <Badge tone="red">off</Badge>}
              </p>
              {tested[c.id] && <p className={tested[c.id] === "Sent" ? "text-xs text-green-600" : "text-xs text-red-600"}>{tested[c.id]}</p>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => test.mutate(c.id)} disabled={test.isPending}>
              <Send size={14} /> Test
            </Button>
            <Button size="icon" variant="ghost" onClick={() => setEditing(c)} aria-label="Edit">
              <Pencil size={16} />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => confirm(`Delete ${c.name}?`) && remove.mutate(c.id)} aria-label="Delete">
              <Trash2 size={16} />
            </Button>
          </li>
        ))}
      </ul>
      {editing && <ChannelForm channel={editing === "new" ? null : editing} plugins={plugins} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function ChannelForm({ channel, plugins, onClose }: { channel: Channel | null; plugins: NotifierPlugin[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [plugin, setPlugin] = useState(channel?.plugin ?? plugins[0]?.id ?? "");
  const [name, setName] = useState(channel?.name ?? "");
  const [enabled, setEnabled] = useState(channel?.enabled ?? true);
  const [config, setConfig] = useState<Record<string, unknown>>(channel?.config ?? {});
  const p = plugins.find((x) => x.id === plugin);
  const props = p?.schema.properties ?? {};
  const required = new Set(p?.schema.required ?? []);
  const save = useMutation({
    mutationFn: () => {
      const body = { plugin, name: name || p?.label || plugin, enabled, config };
      return channel ? api.patch(`/api/notification-channels/${channel.id}`, body) : api.post("/api/notification-channels", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      onClose();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <Modal open onClose={onClose} title={channel ? "Edit channel" : "Add channel"}>
      <form className="space-y-4" onSubmit={submit}>
        <Label label="Type" hint={p?.description}>
          <Select value={plugin} onChange={(e) => setPlugin(e.target.value)} disabled={!!channel}>
            {plugins.map((x) => (
              <option key={x.id} value={x.id}>
                {x.label}
              </option>
            ))}
          </Select>
        </Label>
        <Label label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={p?.label} />
        </Label>
        {Object.entries(props).map(([key, f]) =>
          f.type === "boolean" ? (
            <Checkbox key={key} label={f.description || f.title || key} checked={!!config[key]} onChange={(e) => setConfig({ ...config, [key]: e.target.checked })} />
          ) : (
            <Label key={key} label={f.title || key} hint={f.secret && channel ? `${f.description ?? ""} Leave empty to keep the stored value.`.trim() : f.description}>
              <Input
                type={f.secret ? "password" : f.type === "integer" ? "number" : "text"}
                autoComplete="off"
                value={String(config[key] ?? "")}
                required={required.has(key) && !(f.secret && channel)}
                onChange={(e) => setConfig({ ...config, [key]: f.type === "integer" ? Number(e.target.value) : e.target.value })}
              />
            </Label>
          ),
        )}
        <Checkbox label="Enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
